import React, { useRef, useCallback } from 'react';
import { ATTRIBUTE_COLORS, TYPE_ICONS, CARD_BACK } from '../utils/constants';
import { isSetsunaMagic } from '../engine/keywordAbilities';
import { CARD_SIZE } from '../styles/breakpoints';
import AttributeIcon from './AttributeIcon';
import { useCardIllustration } from '../hooks/useCardIllustration';
import type { CardData, CardType as CardTypeEnum, SkillInfo } from '../types';

// ========================================
// カードコンポーネント
// ========================================

/** Card コンポーネントで扱うカードデータ（拡張版） */
interface CardDisplayData extends CardData {
  currentAttack?: number;
  currentHp?: number;
  isForbidden?: boolean;
}

interface CardProps {
  card: CardDisplayData | null;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  selected?: boolean;
  small?: boolean;
  faceDown?: boolean;
  inHand?: boolean;
  disabled?: boolean;
  modifiedCost?: number;
  costModifierSource?: string;
  onMouseEnter?: (e: React.MouseEvent<HTMLDivElement>) => void;
  onMouseLeave?: (e: React.MouseEvent<HTMLDivElement>) => void;
  isMobile?: boolean;
  onLongPress?: (card: CardDisplayData) => void;
}

const Card: React.FC<CardProps> = ({
  card,
  onClick,
  selected,
  small,
  faceDown,
  inHand,
  disabled,
  modifiedCost,
  costModifierSource,
  onMouseEnter,
  onMouseLeave,
  isMobile,
  onLongPress,
}) => {
  // 長押し検出用
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressTriggeredAt = useRef<number>(0); // タイムスタンプで管理

  const handleTouchStart = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    if (!isMobile || !onLongPress || faceDown || !card) return;
    longPressTriggeredAt.current = 0;
    longPressTimer.current = setTimeout(() => {
      longPressTriggeredAt.current = Date.now();
      onLongPress(card);
    }, 500); // 0.5秒で長押し判定
  }, [isMobile, onLongPress, card, faceDown]);

  const handleTouchEnd = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    // 長押しだった場合はイベント伝播を止める
    if (longPressTriggeredAt.current > 0) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  const handleClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    // 長押し後300ms以内のクリックは無視（タッチイベント→クリックイベントのタイミング差対策）
    if (longPressTriggeredAt.current > 0 && Date.now() - longPressTriggeredAt.current < 300) {
      longPressTriggeredAt.current = 0;
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    longPressTriggeredAt.current = 0;
    if (!disabled && onClick) {
      onClick(e);
    }
  }, [disabled, onClick]);

  // イラストの有無をチェック（共通フックを使用）
  const { hasIllustration, illustrationPath, frameImage } = useCardIllustration(
    card?.id,
    card?.attribute || 'なし'
  );

  if (!card) return null;

  const colors = ATTRIBUTE_COLORS[card.attribute] || ATTRIBUTE_COLORS['なし'];
  const isMonster = card.type === 'monster';

  // モバイル対応サイズ計算
  const getCardSize = (): { width: string; height: string } => {
    if (isMobile) {
      // small指定時（フィールド/フェイズカードエリア）はさらにコンパクト
      if (small) {
        return {
          width: '36px',
          height: '48px',
        };
      }
      // 手札もモンスターエリアと同じサイズを使用
      return {
        width: CARD_SIZE.mobile.slotWidth,
        height: CARD_SIZE.mobile.slotHeight,
      };
    }
    return {
      width: small ? '80px' : '100px',
      height: small ? '110px' : '130px',
    };
  };

  const cardSize = getCardSize();
  const isTiny = isMobile && small; // 超コンパクトモード（フィールド/フェイズカード用）

  const cardStyle: React.CSSProperties = {
    width: cardSize.width,
    height: cardSize.height,
    flexShrink: isMobile && inHand ? 0 : undefined, // 手札横スクロール用
    scrollSnapAlign: isMobile && inHand ? 'start' : undefined,
    borderRadius: isTiny ? '3px' : '8px',
    // イラストがある場合はイラストを背景に、ない場合はsolid枠を背景に
    // 注意: background ショートハンドを使用して個別プロパティとの競合を防ぐ
    background: `url(${hasIllustration ? illustrationPath : frameImage}) center/cover no-repeat`,
    border: selected ? '3px solid #ffd700' : (isTiny ? '1px solid rgba(255,255,255,0.3)' : '2px solid rgba(255,255,255,0.3)'),
    boxShadow: selected
      ? `0 0 20px ${colors.glow}, 0 0 40px rgba(255,215,0,0.5)`
      : (isTiny ? '0 2px 4px rgba(0,0,0,0.3)' : `0 4px 15px rgba(0,0,0,0.4)`),
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.3s ease',
    transform: selected ? 'translateY(-8px) scale(1.05)' : inHand ? 'translateY(0)' : 'none',
    filter: disabled ? 'grayscale(0.8) brightness(0.6)' : 'none',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'relative',
    // モバイル長押し時のOSコンテキストメニュー抑制
    userSelect: 'none',
    WebkitUserSelect: 'none',
    WebkitTouchCallout: 'none',
  };

  if (faceDown) {
    return (
      <div style={{
        ...cardStyle,
        // 注意: background ショートハンドを使用して個別プロパティとの競合を防ぐ
        background: `url(${CARD_BACK}) center/cover no-repeat`,
      }} />
    );
  }

  return (
    <div
      style={cardStyle}
      onClick={handleClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onTouchStart={isMobile ? handleTouchStart : undefined}
      onTouchEnd={isMobile ? handleTouchEnd : undefined}
      onTouchMove={isMobile ? handleTouchEnd : undefined}
    >
      {/* イラストがある場合は透過フレームを前面に重ねる */}
      {hasIllustration && (
        <img
          src={frameImage}
          alt=""
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            pointerEvents: 'none',
            zIndex: 1,
          }}
          draggable={false}
        />
      )}

      {/* コスト表示 */}
      {(() => {
        const actualCost = modifiedCost !== undefined ? modifiedCost : card.cost;
        const isCostReduced = modifiedCost !== undefined && modifiedCost < card.cost;
        const isCostIncreased = modifiedCost !== undefined && modifiedCost > card.cost;
        const costDiff = card.cost - actualCost;

        // ツールチップテキスト生成
        let tooltipText = `コスト: ${card.cost}`;
        if (isCostReduced && costModifierSource) {
          tooltipText = `コスト: ${actualCost} (元: ${card.cost})\n${costModifierSource}により-${costDiff}`;
        } else if (isCostIncreased && costModifierSource) {
          tooltipText = `コスト: ${actualCost} (元: ${card.cost})\n${costModifierSource}により+${-costDiff}`;
        }

        // モバイル時のサイズ: tiny=12px, 通常=18px, デスクトップ=22px
        const costSize = isTiny ? '12px' : (isMobile ? '18px' : '22px');
        const costFontSize = isTiny ? '7px' : (isMobile ? '10px' : '12px');

        return (
          <div
            style={{
              position: 'absolute',
              top: isTiny ? '1px' : (isMobile ? '2px' : '4px'),
              left: isTiny ? '1px' : (isMobile ? '2px' : '4px'),
              width: costSize,
              height: costSize,
              borderRadius: '50%',
              background: isCostReduced
                ? 'linear-gradient(135deg, #22c55e 0%, #4ade80 100%)'
                : isCostIncreased
                  ? 'linear-gradient(135deg, #ef4444 0%, #f87171 100%)'
                  : 'linear-gradient(135deg, #6b4ce6 0%, #9d4ce6 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: costFontSize,
              fontWeight: 'bold',
              color: '#fff',
              boxShadow: isCostReduced
                ? '0 2px 8px rgba(34, 197, 94, 0.6)'
                : isCostIncreased
                  ? '0 2px 8px rgba(239, 68, 68, 0.6)'
                  : '0 2px 8px rgba(0,0,0,0.4)',
            }}
            title={tooltipText}
          >
            {actualCost}
          </div>
        );
      })()}

      {/* 禁忌カード表示（tinyでは非表示） */}
      {card.isForbidden && !isTiny && (
        <div style={{
          position: 'absolute',
          top: '4px',
          left: '28px',
          fontSize: '14px',
          filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.8))',
        }}>
          ⚠️
        </div>
      )}

      {/* 刹那詠唱マーカー（tinyでは非表示） */}
      {isSetsunaMagic(card) && !isTiny && (
        <div
          style={{
            position: 'absolute',
            top: '4px',
            left: card.isForbidden ? '44px' : '28px',
            width: '18px',
            height: '18px',
            borderRadius: '3px',
            background: 'linear-gradient(135deg, #00bcd4 0%, #4dd0e1 100%)',
            border: '1px solid rgba(255, 255, 255, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '11px',
            boxShadow: '0 0 8px rgba(0, 188, 212, 0.9), 0 0 16px rgba(0, 188, 212, 0.4)',
          }}
          title="【刹那詠唱】相手ターンでも発動可能（コスト+1）"
        >
          ⚡
        </div>
      )}

      {/* タイプアイコン（tinyでは非表示） */}
      {!isTiny && (
        <div style={{
          position: 'absolute',
          top: '4px',
          right: '4px',
          fontSize: '14px',
        }}>
          {TYPE_ICONS[card.type]}
        </div>
      )}

      {/* カード名（tinyでは非表示） */}
      {!isTiny && (
        <div style={{
          padding: isMobile ? '22px 2px 2px' : '26px 4px 4px',
          fontSize: isMobile ? CARD_SIZE.mobile.fontSize : (small ? '9px' : '10px'),
          fontWeight: 'bold',
          textAlign: 'center',
          color: colors.text,
          textShadow: '0 1px 2px rgba(0,0,0,0.5)',
          lineHeight: '1.2',
          height: isMobile ? '28px' : '36px',
          overflow: 'hidden',
        }}>
          {card.name}
        </div>
      )}

      {/* イラストエリア */}
      <div style={{
        flex: 1,
        margin: isTiny ? '0 1px' : '2px 4px',
        borderRadius: isTiny ? '2px' : '4px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: isTiny ? '1px' : '2px',
        position: 'relative',
      }}>
        <AttributeIcon
          attribute={card.attribute}
          size={isTiny ? 16 : (isMobile ? 28 : 36)}
        />

        {/* 技アイコン表示（モンスターのみ、tinyでは非表示） */}
        {!isTiny && card.type === 'monster' && (card.basicSkill || card.advancedSkill) && (
          <div style={{
            position: 'absolute',
            bottom: '2px',
            right: '2px',
            display: 'flex',
            gap: '2px',
          }}>
            {card.basicSkill && (
              <div style={{
                width: '16px',
                height: '16px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                fontWeight: 'bold',
                color: '#fff',
                boxShadow: '0 1px 3px rgba(0,0,0,0.5)',
              }} title={`基本技: ${card.basicSkill.text}`}>
                1
              </div>
            )}
            {card.advancedSkill && (
              <div style={{
                width: '16px',
                height: '16px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #ff9800 0%, #ffa726 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                fontWeight: 'bold',
                color: '#fff',
                boxShadow: '0 1px 3px rgba(0,0,0,0.5)',
              }} title={`上級技: ${card.advancedSkill.text}`}>
                2
              </div>
            )}
          </div>
        )}
      </div>

      {/* ステータス（モンスターのみ、tinyでは非表示） */}
      {isMonster && !isTiny && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '4px 6px',
          background: 'rgba(0,0,0,0.4)',
          fontSize: small ? '10px' : '11px',
          fontWeight: 'bold',
        }}>
          <span style={{ color: '#fff' }}>⚔️{card.currentAttack || card.attack}</span>
          <span style={{ color: '#6bff6b' }}>❤️{card.currentHp || card.hp}</span>
        </div>
      )}
    </div>
  );
};

export default Card;
